// Section 9: the build queue. The player orders it; builders pour all their points into the
// first unpaused item that can proceed and leftover points flow to the next item. Materials are
// deducted when construction starts: an item whose materials are missing is blocked (warning
// `build-blocked`) and skipped, so the next item that can start takes the points. An item that
// would exceed its building's limit waits (warning `build-limit`), checked at start and again at
// completion. A finished order becomes a building at condition 100 (`building-complete`, the
// onComplete hook; queued shelters and wells are promised builds, Section 16) or restores the
// building it repairs (`building-repaired`). Repairs cost points and materials by damage (B-05).
// Constants: config.building, config.buildings.*.
import { RESOURCES } from '../../core/constants';
import { mintId } from '../../core/ids';
import { invalidateMemo } from '../../core/memo';
import { BUILDING_MEMO, buildingCount } from '../../core/queries';
import type { BuildOrder, Building, PhaseContext, Resource } from '../../core/types';
import {
  buildingLimit,
  missingMaterial,
  recomputeCapacity,
  repairMaterials,
  repairShare,
} from '../shared/capacity';
import { applyDeltas } from '../shared/deltas';
import { addStock } from '../shared/stocks';

const EPS = 1e-9;

/** A new building of this type would exceed its limit (repairs never do). */
function atLimit(ctx: PhaseContext, order: BuildOrder): boolean {
  if (order.repairOf !== null) return false;
  const type = order.buildingType;
  return buildingCount(ctx.draft, type) >= buildingLimit(ctx.registry, ctx.draft, type);
}

function startCost(ctx: PhaseContext, order: BuildOrder): Partial<Record<Resource, number>> {
  if (order.repairOf === null) return ctx.registry.buildings[order.buildingType].materials;
  const b = ctx.draft.buildings[order.repairOf];
  return b === undefined ? {} : repairMaterials(ctx.registry, b);
}

/** Start construction: pay the materials, or report why the item cannot start. */
function start(ctx: PhaseContext, order: BuildOrder): boolean {
  const buildingType = order.buildingType;
  if (atLimit(ctx, order)) {
    ctx.report.add({ kind: 'warning', code: 'build-limit', buildingType });
    return false;
  }
  const cost = startCost(ctx, order);
  const missing = missingMaterial(ctx.draft, cost);
  if (missing !== null) {
    ctx.report.add({
      kind: 'warning',
      code: 'build-blocked',
      resource: missing.resource,
      amount: missing.short,
      buildingType,
    });
    return false;
  }
  const spent: Partial<Record<Resource, number>> = {};
  for (const r of RESOURCES) {
    const n = cost[r] ?? 0;
    if (n <= 0) continue;
    addStock(ctx, r, -n, 'other');
    spent[r] = n;
  }
  // A cancel refunds a share of what was actually paid (M6, decide.ts).
  order.materialsSpent = spent;
  if (order.repairOf !== null) {
    const b = ctx.draft.buildings[order.repairOf];
    order.pointsRequired = b === undefined ? 0 : order.pointsRequired * repairShare(b);
  }
  order.materialsPaid = true;
  return true;
}

function complete(ctx: PhaseContext, order: BuildOrder): void {
  const d = ctx.draft;
  const turn = ctx.season.turn;
  const interval = ctx.cfg.building.upkeepEverySeasons;
  if (order.repairOf !== null) {
    const b = d.buildings[order.repairOf];
    if (b === undefined) return;
    b.condition = 100;
    b.upkeepDueTurn = turn + interval;
    invalidateMemo(d, BUILDING_MEMO);
    ctx.report.add({ kind: 'building-repaired', buildingId: b.id, buildingType: b.type });
    recomputeCapacity(ctx.live, d);
    return;
  }
  const id = mintId(d, 'building');
  const building: Building = {
    id,
    type: order.buildingType,
    condition: 100,
    builtTurn: turn,
    upkeepDueTurn: turn + interval,
    frozen: false,
  };
  d.buildings[id] = building;
  invalidateMemo(d, BUILDING_MEMO);
  ctx.report.add({
    kind: 'building-complete',
    buildingId: id,
    buildingType: order.buildingType,
    promised: ctx.cfg.building.promisedBuildTypes.includes(order.buildingType),
  });
  applyDeltas(ctx, ctx.registry.buildings[order.buildingType].onComplete(ctx.live, building));
  recomputeCapacity(ctx.live, d);
}

/** Spend this season's build points down the queue (Section 9 build queue rules). */
export function construct(ctx: PhaseContext, points: number): void {
  let left = points;
  const done = new Set<string>();
  for (const order of ctx.draft.buildQueue) {
    if (order.paused) continue;
    if (order.repairOf !== null && ctx.draft.buildings[order.repairOf] === undefined) {
      done.add(order.id); // the building it repairs is gone
      continue;
    }
    // A fully paid order waiting on its limit is re-checked every season, points or not.
    const finished = order.materialsPaid && order.pointsRequired - order.pointsPaid <= EPS;
    if (!finished) {
      if (left <= EPS) continue;
      if (!order.materialsPaid && !start(ctx, order)) continue;
      const spend = Math.min(left, Math.max(0, order.pointsRequired - order.pointsPaid));
      order.pointsPaid += spend;
      left -= spend;
      const remaining = Math.max(0, order.pointsRequired - order.pointsPaid);
      if (spend > 0)
        ctx.report.add({
          kind: 'construction',
          orderId: order.id,
          buildingType: order.buildingType,
          points: spend,
          remaining,
        });
      if (remaining > EPS) continue;
    }
    if (atLimit(ctx, order)) {
      ctx.report.add({ kind: 'warning', code: 'build-limit', buildingType: order.buildingType });
      continue;
    }
    complete(ctx, order);
    done.add(order.id);
  }
  if (done.size > 0) ctx.draft.buildQueue = ctx.draft.buildQueue.filter((o) => !done.has(o.id));
}
